/**
 * MFA Management Modal Component
 * Handles MFA setup and disable flows with proper state management
 */

'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Steps, Button, Input, Alert, Typography, Space, Divider, App } from 'antd';
import Image from 'next/image';
import { QrcodeOutlined, KeyOutlined, SafetyOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useAuth } from '@/domains/auth/context/auth-context';
import { useTheme } from '@/shared/theme';

const { Title, Text, Paragraph } = Typography;
interface MFAManagementModalProps {
  open: boolean;
  onClose: () => void;
  mode: 'setup' | 'disable';
  onSuccess: () => void;
}

interface MFASetupData {
  qr_code?: string;
  secret?: string;
  backup_codes?: string[];
}

const MFAManagementModal: React.FC<MFAManagementModalProps> = ({
  open,
  onClose,
  mode,
  onSuccess
}) => {
  const { message } = App.useApp();
  const { token } = useTheme();
  const { setupMFA, verifyMFASetup, disableMFA, user } = useAuth();

  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mfaSetupData, setMfaSetupData] = useState<MFASetupData | null>(null);
  const [verificationToken, setVerificationToken] = useState('');
  const [disableToken, setDisableToken] = useState('');

  // Reset state when modal opens/closes
  const initializeMFASetup = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      console.log('Initializing MFA setup...');
      const result = await setupMFA();
      console.log('MFA setup result:', result);

      if (result.success && result.data) {
        console.log('MFA setup successful, data received:', result.data);
        setMfaSetupData(result.data);
      } else {
        console.error('MFA setup failed:', result.error);
        let errorMsg = result.error || 'Failed to initialize MFA setup';

        // Check if it's a server error (500)
        if (result.error?.includes('500') || result.error?.includes('Internal Server Error')) {
          errorMsg = 'Server error: MFA service is temporarily unavailable. Please try again later.';
        }

        setError(errorMsg);
        message.error(`MFA Setup Error: ${errorMsg}`);
      }
    } catch (error) {
      console.error('MFA setup initialization error:', error);
      const errorMsg = error instanceof Error ? error.message : 'Failed to initialize MFA setup';
      setError(errorMsg);
      message.error(`MFA Setup Error: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  }, [setupMFA]);

  useEffect(() => {
    if (open) {
      setCurrentStep(0);
      setError(null);
      setMfaSetupData(null);
      setVerificationToken('');
      setDisableToken('');

      if (mode === 'setup') {
        initializeMFASetup();
      }
    }
  }, [open, mode, initializeMFASetup]);

  const handleVerifySetup = async () => {
    if (!verificationToken.trim()) {
      setError('Please enter the verification code');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await verifyMFASetup(verificationToken);

      if (result.success) {
        message.success('MFA setup completed successfully!');
        onSuccess();
        onClose();
      } else {
        setError(result.error || 'MFA verification failed');
      }
    } catch (error) {
      console.error('MFA verification error:', error);
      setError(error instanceof Error ? error.message : 'MFA verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleDisableMFA = async () => {
    if (!disableToken.trim()) {
      setError('Please enter your MFA code to disable 2FA');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await disableMFA(disableToken);

      if (result.success) {
        message.success('MFA has been disabled successfully');
        onSuccess();
        onClose();
      } else {
        setError(result.error || 'Failed to disable MFA');
      }
    } catch (error) {
      console.error('MFA disable error:', error);
      setError(error instanceof Error ? error.message : 'Failed to disable MFA');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      message.success(`${label} copied to clipboard`);
    } catch (error) {
      message.error('Failed to copy to clipboard');
    }
  };

  const downloadBackupCodes = () => {
    if (!mfaSetupData?.backup_codes) return;

    const content = `Mechagora Trading Platform - MFA Backup Codes

IMPORTANT: Keep these backup codes in a safe place. You can use them to access your account if you lose your authenticator device.

Backup Codes:
${mfaSetupData.backup_codes.map((code, index) => `${index + 1}. ${code}`).join('\n')}

Generated on: ${new Date().toLocaleString()}
`;

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'mechagora-mfa-backup-codes.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const renderSetupContent = () => {
    if (loading && !mfaSetupData) {
      return (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Title level={3}>Setting up MFA...</Title>
          <Text type="secondary">Please wait while we generate your MFA setup</Text>
        </div>
      );
    }

    if (error && !mfaSetupData) {
      const isServerError = error.includes('Server error') || error.includes('500') || error.includes('Internal Server Error');

      return (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Alert
            title={isServerError ? "Server Temporarily Unavailable" : "MFA Setup Error"}
            description={
              isServerError
                ? "The MFA service is currently experiencing issues. This is a temporary problem on our servers. Please try again in a few minutes."
                : error
            }
            type="error"
            showIcon
            style={{ marginBottom: '16px' }}
          />
          <Button type="primary" onClick={initializeMFASetup} loading={loading}>
            {isServerError ? "Retry Now" : "Try Again"}
          </Button>
        </div>
      );
    }

    if (!mfaSetupData) {
      return null;
    }

    return (
      <div style={{ padding: '24px 0' }}>
        <Steps
          current={currentStep}
          style={{ marginBottom: '32px' }}
          items={[
            { title: 'Scan QR Code', icon: <QrcodeOutlined /> },
            { title: 'Backup Codes', icon: <KeyOutlined /> },
            { title: 'Verify Setup', icon: <SafetyOutlined /> },
          ]}
        />

        {currentStep === 0 && (
          <div style={{ textAlign: 'center' }}>
            <Title level={4}>Scan QR Code</Title>
            <Paragraph>
              Open your authenticator app (Google Authenticator, Authy, etc.) and scan this QR code:
            </Paragraph>

            <div style={{ margin: '24px 0' }}>
              <Image
                src={mfaSetupData.qr_code || ''}
                alt="MFA QR Code"
                width={200}
                height={200}
                style={{
                  border: `1px solid ${token.colorBorder}`,
                  borderRadius: '8px',
                  maxWidth: '200px'
                }}
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <Text strong>Manual Entry Secret:</Text>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                justifyContent: 'center',
                marginTop: '8px'
              }}>
                <Input
                  value={mfaSetupData.secret || ''}
                  readOnly
                  style={{ fontFamily: 'monospace', maxWidth: '200px' }}
                />
                <Button
                  size="small"
                  onClick={() => copyToClipboard(mfaSetupData.secret || '', 'Secret key')}
                >
                  Copy
                </Button>
              </div>
            </div>

            <Button type="primary" onClick={() => setCurrentStep(1)}>
              Next: Backup Codes
            </Button>
          </div>
        )}

        {currentStep === 1 && (
          <div style={{ textAlign: 'center' }}>
            <Title level={4}>Backup Codes</Title>
            <Paragraph>
              Save these backup codes in a secure location. You can use them to access your account if you lose your authenticator device.
            </Paragraph>

            <Alert
              title="Important"
              description="Each backup code can only be used once. Generate new codes if you run out."
              type="warning"
              showIcon
              style={{ marginBottom: '24px' }}
            />

            <div style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '8px',
              marginBottom: '24px'
            }}>
              <Button onClick={downloadBackupCodes}>
                Download Codes
              </Button>
              <Button
                onClick={() => copyToClipboard((mfaSetupData.backup_codes || []).join('\n'), 'Backup codes')}
              >
                Copy All
              </Button>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '8px',
              maxWidth: '400px',
              margin: '0 auto 24px'
            }}>
              {(mfaSetupData.backup_codes || []).map((code, index) => (
                <div
                  key={index}
                  style={{
                    padding: '8px',
                    border: `1px solid ${token.colorBorder}`,
                    borderRadius: '4px',
                    fontFamily: 'monospace',
                    fontSize: '14px',
                    textAlign: 'center',
                    backgroundColor: token.colorBgContainer
                  }}
                >
                  {code}
                </div>
              ))}
            </div>

            <Space>
              <Button onClick={() => setCurrentStep(0)}>
                Previous
              </Button>
              <Button type="primary" onClick={() => setCurrentStep(2)}>
                Next: Verify Setup
              </Button>
            </Space>
          </div>
        )}

        {currentStep === 2 && (
          <div style={{ textAlign: 'center' }}>
            <Title level={4}>Verify Setup</Title>
            <Paragraph>
              Enter the 6-digit code from your authenticator app to confirm MFA is working correctly.
            </Paragraph>

            <div style={{ margin: '24px 0' }}>
              <Input
                placeholder="Enter 6-digit code"
                value={verificationToken}
                onChange={(e) => setVerificationToken(e.target.value)}
                maxLength={6}
                style={{
                  fontSize: '18px',
                  textAlign: 'center',
                  letterSpacing: '2px',
                  maxWidth: '200px'
                }}
              />
            </div>

            {error && (
              <Alert
                title={error}
                type="error"
                showIcon
                style={{ marginBottom: '16px' }}
              />
            )}

            <Space>
              <Button onClick={() => setCurrentStep(1)}>
                Previous
              </Button>
              <Button
                type="primary"
                onClick={handleVerifySetup}
                loading={loading}
                disabled={!verificationToken.trim()}
              >
                Complete Setup
              </Button>
            </Space>
          </div>
        )}
      </div>
    );
  };

  const renderDisableContent = () => {
    return (
      <div style={{ padding: '24px 0', textAlign: 'center' }}>
        <Title level={4}>Disable Two-Factor Authentication</Title>

        <Alert
          title="Security Warning"
          description="Disabling 2FA will reduce the security of your account. Make sure this is what you want to do."
          type="warning"
          showIcon
          style={{ marginBottom: '24px' }}
        />

        <Paragraph>
          To disable 2FA, please enter the current 6-digit code from your authenticator app.
        </Paragraph>

        <div style={{ margin: '24px 0' }}>
          <Input
            placeholder="Enter 6-digit code"
            value={disableToken}
            onChange={(e) => setDisableToken(e.target.value)}
            maxLength={6}
            style={{
              fontSize: '18px',
              textAlign: 'center',
              letterSpacing: '2px',
              maxWidth: '200px'
            }}
          />
        </div>

        {error && (
          <Alert
            title={error}
            type="error"
            showIcon
            style={{ marginBottom: '16px' }}
          />
        )}

        <Space>
          <Button onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="primary"
            danger
            onClick={handleDisableMFA}
            loading={loading}
            disabled={!disableToken.trim()}
          >
            Disable 2FA
          </Button>
        </Space>
      </div>
    );
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <SafetyOutlined />
          {mode === 'setup' ? 'Setup Two-Factor Authentication' : 'Disable Two-Factor Authentication'}
        </div>
      }
      open={open}
      onCancel={onClose}
      footer={null}
      width={600}
      centered
      destroyOnHidden
    >
      {mode === 'setup' ? renderSetupContent() : renderDisableContent()}
    </Modal>
  );
};

export default MFAManagementModal;
